import { queryOptions } from '@tanstack/react-query';
import type { Me } from './types';

/** Thin fetch wrapper: same-origin, credentials included, throws on non-2xx. */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  // An empty body has nothing to parse. 204 No Content is the normal reply to a
  // mutation here, and calling res.json() on it THROWS — which made successful writes
  // report failure: the error toast fired, onSuccess never ran so the list never
  // refetched, and the change only appeared after a manual reload.
  const text = await res.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The identity + RBAC-filtered nav, the single source the shell renders from. */
export const meQueryOptions = queryOptions({
  queryKey: ['me'],
  queryFn: () => apiFetch<Me>('/api/me'),
  retry: false,
  staleTime: 60_000,
});
